import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { 
  Loader2, Film, Music, Play, Pause, SkipBack, SkipForward
} from "lucide-react";
import { getAssetUrl } from "@/utils/studioUtils";

/**
 * Convert seconds to timecode format MM:SS;FF (assuming 24fps)
 */
const formatTimecode = (seconds, fps = 24) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return "00:00;00";
  const totalFrames = Math.floor(seconds * fps);
  const frames = totalFrames % fps;
  const totalSeconds = Math.floor(seconds);
  const secs = totalSeconds % 60;
  const mins = Math.floor(totalSeconds / 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')};${String(frames).padStart(2, '0')}`;
};

/**
 * Generate time ruler markers based on duration
 * Now generates markers for the VISIBLE (trimmed) duration only
 */
const generateTimeMarkers = (visibleDuration, pixelsPerSecond) => {
  const markers = [];
  if (!visibleDuration || visibleDuration <= 0) return markers;
  
  // Determine marker interval based on zoom level
  let majorInterval = 1; // seconds
  if (pixelsPerSecond < 30) majorInterval = 5;
  else if (pixelsPerSecond < 60) majorInterval = 2;
  else if (pixelsPerSecond > 150) majorInterval = 0.5;
  
  for (let t = 0; t <= visibleDuration; t += majorInterval) {
    markers.push({
      time: t,
      label: formatTimecode(t),
      isMajor: true
    });
  }
  
  // Add minor markers (half interval)
  const minorInterval = majorInterval / 2;
  for (let t = minorInterval; t < visibleDuration; t += majorInterval) {
    markers.push({
      time: t,
      label: null,
      isMajor: false
    });
  }
  
  return markers.sort((a, b) => a.time - b.time);
};

/**
 * LipsyncStage - Professional video editor with Apple Motion-style timeline
 */
export function LipsyncStage({
  scene,
  videoPreviewRef,
  audioPreviewRef,
  uploadedSceneVideo,
  animationTakes,
  activeAnimationIndex,
  applyingLipSync,
  selectedVoiceForMerge,
  assets,
  videoDuration,
  setVideoDuration,
  audioDuration,
  setAudioDuration,
  playheadPosition,
  setPlayheadPosition,
  trimStart,
  setTrimStart,
  trimEnd,
  setTrimEnd,
  audioOffset,
  setAudioOffset,
  isPreviewPlaying,
  onTogglePreviewPlayback,
  onVideoEnded,
  isDraggingTrim,
  setIsDraggingTrim,
  // Server-side preview props
  syncPreviewUrl,
  generatingSyncPreview,
  onGenerateSyncPreview,
  onClearSyncPreview
}) {
  const timelineRef = useRef(null);
  const syncPreviewRef = useRef(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [isDraggingAudio, setIsDraggingAudio] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartOffset, setDragStartOffset] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);
  const [showSyncPreview, setShowSyncPreview] = useState(false); // Toggle between timeline and preview view
  
  // Calculate effective duration (after trim)
  const effectiveDuration = (trimEnd || videoDuration) - trimStart;
  
  // Timeline width in pixels - fixed width for consistency
  const timelineWidth = 800;
  // Use full video duration for timeline scale (shows trim handles)
  const pixelsPerSecond = videoDuration > 0 ? timelineWidth / videoDuration : 100;
  
  // Get the voice asset file URL
  const voiceAsset = assets?.find(a => a.id === selectedVoiceForMerge);
  const voiceFileUrl = voiceAsset?.file_url;
  
  // Time markers for the ruler - based on full video duration
  const timeMarkers = generateTimeMarkers(videoDuration, pixelsPerSecond);
  
  // Convert current playhead position to effective time (relative to trim start)
  // This is for the timecode display
  const effectivePlayheadPosition = Math.max(0, playheadPosition - trimStart);
  
  // Load audio waveform data
  useEffect(() => {
    if (!voiceFileUrl) {
      setWaveformData([]);
      return;
    }
    
    const loadWaveform = async () => {
      setIsLoadingWaveform(true);
      try {
        const audioUrl = getAssetUrl(voiceFileUrl);
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Get audio data from first channel
        const channelData = audioBuffer.getChannelData(0);
        const sampleRate = audioBuffer.sampleRate;
        
        // Calculate samples per pixel (aim for ~200 data points for the waveform)
        const targetPoints = 200;
        const samplesPerPoint = Math.floor(channelData.length / targetPoints);
        
        const waveform = [];
        for (let i = 0; i < targetPoints; i++) {
          const start = i * samplesPerPoint;
          const end = Math.min(start + samplesPerPoint, channelData.length);
          
          // Calculate RMS (root mean square) for this segment
          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += channelData[j] * channelData[j];
          }
          const rms = Math.sqrt(sum / (end - start));
          
          // Normalize to 0-1 range and amplify for visibility
          const normalizedValue = Math.min(1, rms * 3);
          waveform.push(normalizedValue);
        }
        
        setWaveformData(waveform);
        audioContext.close();
      } catch (error) {
        console.error('Error loading waveform:', error);
        // Fallback to generated waveform
        setWaveformData([]);
      } finally {
        setIsLoadingWaveform(false);
      }
    };
    
    loadWaveform();
  }, [voiceFileUrl]);
  
  // Show sync preview automatically when URL is available
  useEffect(() => {
    if (syncPreviewUrl) {
      setShowSyncPreview(true);
    }
  }, [syncPreviewUrl]);
  
  // Clear sync preview when timeline settings change (user needs to regenerate)
  useEffect(() => {
    if (onClearSyncPreview) {
      onClearSyncPreview();
    }
    setShowSyncPreview(false);
  }, [audioOffset, trimStart, trimEnd]);
  
  // Handle playhead drag - sync audio when seeking
  const handleTimelineMouseDown = useCallback((e) => {
    if (!timelineRef.current || !videoPreviewRef?.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * videoDuration;
    
    // Clamp to trim bounds
    const clampedTime = Math.max(trimStart, Math.min(trimEnd || videoDuration, newTime));
    videoPreviewRef.current.currentTime = clampedTime;
    setPlayheadPosition(clampedTime);
    
    // Sync audio to new position
    if (audioPreviewRef?.current) {
      const audioTime = clampedTime - audioOffset;
      console.log('[Timeline Click] Seeking to:', { clampedTime, audioOffset, audioTime });
      if (audioTime >= 0 && audioTime < audioDuration) {
        audioPreviewRef.current.currentTime = audioTime;
      }
    }
    
    setIsDraggingPlayhead(true);
  }, [videoDuration, trimStart, trimEnd, audioOffset, audioDuration, videoPreviewRef, audioPreviewRef, setPlayheadPosition]);
  
  const handleTimelineMouseMove = useCallback((e) => {
    if (!isDraggingPlayhead || !timelineRef.current || !videoPreviewRef?.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * videoDuration;
    
    // Clamp to trim bounds
    const clampedTime = Math.max(trimStart, Math.min(trimEnd || videoDuration, newTime));
    videoPreviewRef.current.currentTime = clampedTime;
    setPlayheadPosition(clampedTime);
    
    // Sync audio to new position
    if (audioPreviewRef?.current) {
      const audioTime = clampedTime - audioOffset;
      if (audioTime >= 0 && audioTime < audioDuration) {
        audioPreviewRef.current.currentTime = audioTime;
      }
    }
  }, [isDraggingPlayhead, videoDuration, trimStart, trimEnd, audioOffset, audioDuration, videoPreviewRef, audioPreviewRef, setPlayheadPosition]);
  
  const handleMouseUp = useCallback(() => {
    setIsDraggingPlayhead(false);
    setIsDraggingAudio(false);
    setIsDraggingTrim(false);
  }, [setIsDraggingTrim]);
  
  // Handle audio clip drag
  const handleAudioDragStart = useCallback((e) => {
    e.stopPropagation();
    setIsDraggingAudio(true);
    setDragStartX(e.clientX);
    setDragStartOffset(audioOffset);
  }, [audioOffset]);
  
  const handleAudioDrag = useCallback((e) => {
    if (!isDraggingAudio || !timelineRef.current) return;
    
    const deltaX = e.clientX - dragStartX;
    const deltaSeconds = deltaX / pixelsPerSecond;
    const newOffset = Math.max(-audioDuration, Math.min(videoDuration, dragStartOffset + deltaSeconds));
    console.log('[Audio Drag] Setting audioOffset to:', newOffset, { deltaX, deltaSeconds, dragStartOffset });
    setAudioOffset(newOffset);
  }, [isDraggingAudio, dragStartX, dragStartOffset, pixelsPerSecond, audioDuration, videoDuration, setAudioOffset]);
  
  // Handle trim handle drag
  const handleTrimStartDrag = useCallback((e) => {
    e.stopPropagation();
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTrimStart = Math.min(percentage * videoDuration, (trimEnd || videoDuration) - 0.5);
    setTrimStart(Math.max(0, newTrimStart));
  }, [videoDuration, trimEnd, setTrimStart]);
  
  const handleTrimEndDrag = useCallback((e) => {
    e.stopPropagation();
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTrimEnd = Math.max(percentage * videoDuration, trimStart + 0.5);
    setTrimEnd(Math.min(videoDuration, newTrimEnd));
  }, [videoDuration, trimStart, setTrimEnd]);
  
  // Global mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      handleTimelineMouseMove(e);
      handleAudioDrag(e);
    };
    
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleTimelineMouseMove, handleAudioDrag, handleMouseUp]);
  
  // Skip to start/end of trim region with audio sync
  const skipToStart = () => {
    if (videoPreviewRef?.current) {
      videoPreviewRef.current.currentTime = trimStart;
      setPlayheadPosition(trimStart);
      // Sync audio
      if (audioPreviewRef?.current) {
        const audioTime = trimStart - audioOffset;
        if (audioTime >= 0 && audioTime < audioDuration) {
          audioPreviewRef.current.currentTime = audioTime;
        } else {
          audioPreviewRef.current.currentTime = 0;
        }
      }
    }
  };
  
  const skipToEnd = () => {
    if (videoPreviewRef?.current) {
      const endTime = trimEnd || videoDuration;
      videoPreviewRef.current.currentTime = endTime;
      setPlayheadPosition(endTime);
      // Sync audio
      if (audioPreviewRef?.current) {
        const audioTime = endTime - audioOffset;
        if (audioTime >= 0 && audioTime < audioDuration) {
          audioPreviewRef.current.currentTime = audioTime;
        }
      }
    }
  };

  return (
    <div className="flex-1 flex flex-col p-4 overflow-hidden" data-testid="lipsync-stage">
      {/* Video Display */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <div 
          className="relative rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950"
          style={{ 
            aspectRatio: "16/9",
            width: '100%',
            maxWidth: 'min(900px, calc((100vh - 400px) * 16 / 9))',
            maxHeight: 'calc(100vh - 400px)'
          }}
        >
          {/* Show sync preview video when available and selected */}
          {showSyncPreview && syncPreviewUrl ? (
            <video
              ref={syncPreviewRef}
              key={`sync-preview-${syncPreviewUrl}`}
              src={syncPreviewUrl}
              controls
              autoPlay
              className="w-full h-full object-contain"
            />
          ) : uploadedSceneVideo ? (
            <video
              ref={videoPreviewRef}
              key={`preview-uploaded-${uploadedSceneVideo}`}
              src={getAssetUrl(uploadedSceneVideo)}
              onEnded={onVideoEnded}
              onLoadedMetadata={(e) => {
                setVideoDuration(e.target.duration);
                if (trimEnd === null) setTrimEnd(e.target.duration);
              }}
              onTimeUpdate={(e) => setPlayheadPosition(e.target.currentTime)}
              muted={!!selectedVoiceForMerge}
              className="w-full h-full object-contain"
            />
          ) : animationTakes.length > 0 && animationTakes[activeAnimationIndex]?.video_url ? (
            <video
              ref={videoPreviewRef}
              key={`preview-${animationTakes[activeAnimationIndex].video_url}`}
              src={getAssetUrl(animationTakes[activeAnimationIndex].video_url)}
              onEnded={onVideoEnded}
              onLoadedMetadata={(e) => {
                setVideoDuration(e.target.duration);
                if (trimEnd === null) setTrimEnd(e.target.duration);
              }}
              onTimeUpdate={(e) => setPlayheadPosition(e.target.currentTime)}
              muted={!!selectedVoiceForMerge}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No video available</p>
                <p className="text-xs mt-1">Go back and upload or generate a video</p>
              </div>
            </div>
          )}

          {/* Sync Preview Badge */}
          {showSyncPreview && syncPreviewUrl && (
            <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Play className="w-3 h-3" />
              Synced Preview
            </div>
          )}

          {/* Loading Overlay for Sync Preview Generation */}
          {generatingSyncPreview && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-20">
              <div className="text-center">
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-green-500 animate-spin" />
                <p className="text-white font-medium">Generating Synced Preview...</p>
                <p className="text-sm text-zinc-400 mt-1">Merging audio and video with FFmpeg</p>
              </div>
            </div>
          )}

          {/* Loading Overlay for Lip Sync Apply */}
          {applyingLipSync && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-20">
              <div className="text-center">
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-500 animate-spin" />
                <p className="text-white font-medium">Generating Lip Sync...</p>
                <p className="text-sm text-zinc-400 mt-1">Usually takes 1-2 minutes</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden audio element with crossOrigin for CORS */}
      {selectedVoiceForMerge && voiceFileUrl && (
        <audio
          ref={audioPreviewRef}
          src={getAssetUrl(voiceFileUrl)}
          preload="auto"
          crossOrigin="anonymous"
          onLoadedMetadata={(e) => setAudioDuration(e.target.duration)}
          onCanPlayThrough={(e) => console.log('[Audio] Can play through, duration:', e.target.duration)}
        />
      )}

      {/* Timeline Editor - Apple Motion Style */}
      {selectedVoiceForMerge && (uploadedSceneVideo || animationTakes.length > 0) && (
        <div className="mt-4 bg-zinc-900 rounded-lg border border-zinc-700 overflow-hidden shrink-0">
          {/* Transport Controls & Timecode Display */}
          <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-b border-zinc-700">
            <div className="flex items-center gap-2">
              {/* View Toggle - Edit vs Preview */}
              {syncPreviewUrl && (
                <div className="flex items-center bg-zinc-700 rounded-lg p-0.5 mr-2">
                  <Button
                    onClick={() => setShowSyncPreview(false)}
                    size="sm"
                    variant="ghost"
                    className={`h-6 px-2 text-xs rounded ${!showSyncPreview ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => setShowSyncPreview(true)}
                    size="sm"
                    variant="ghost"
                    className={`h-6 px-2 text-xs rounded ${showSyncPreview ? 'bg-green-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                  >
                    Preview
                  </Button>
                </div>
              )}
              
              {/* Transport buttons - hide when showing sync preview */}
              {!showSyncPreview && (
                <>
                  <Button
                    onClick={skipToStart}
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                  >
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={onTogglePreviewPlayback}
                    size="sm"
                    variant="ghost"
                    className={`h-7 w-7 p-0 ${isPreviewPlaying ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}`}
                  >
                    {isPreviewPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={skipToEnd}
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                  >
                    <SkipForward className="w-4 h-4" />
                  </Button>
                </>
              )}
              
              {/* Generate Sync Preview Button */}
              <Button
                onClick={onGenerateSyncPreview}
                disabled={generatingSyncPreview || !selectedVoiceForMerge}
                size="sm"
                className="h-7 px-3 bg-green-600 hover:bg-green-500 text-white text-xs ml-2"
              >
                {generatingSyncPreview ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : syncPreviewUrl ? (
                  <>
                    <Play className="w-3 h-3 mr-1" />
                    Regenerate Preview
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3 mr-1" />
                    Preview Sync
                  </>
                )}
              </Button>
            </div>
            
            {/* Timecode Display - Current / Total */}
            {!showSyncPreview && (
              <div className="flex items-center gap-1 font-mono text-sm">
                <span className="text-white bg-zinc-700 px-2 py-0.5 rounded">
                  {formatTimecode(effectivePlayheadPosition)}
                </span>
                <span className="text-zinc-500">/</span>
                <span className="text-zinc-400 bg-zinc-700/50 px-2 py-0.5 rounded">
                  {formatTimecode(effectiveDuration)}
                </span>
              </div>
            )}
            
            {/* Sync Preview Info */}
            {showSyncPreview && syncPreviewUrl && (
              <div className="text-xs text-green-400 flex items-center gap-1">
                <span>✓ Preview ready - use native controls to play</span>
              </div>
            )}
            
            {/* Trim Info */}
            {!showSyncPreview && (trimStart > 0 || (trimEnd !== null && trimEnd < videoDuration)) && (
              <div className="text-xs text-amber-400 flex items-center gap-1">
                <span>Trimmed:</span>
                <span className="font-mono">{formatTimecode(trimStart)} → {formatTimecode(trimEnd || videoDuration)}</span>
              </div>
            )}
          </div>

          {/* Timeline Editor - hide when showing sync preview */}
          {!showSyncPreview && (
            <>
          {/* Time Ruler */}
          <div 
            className="relative h-6 bg-zinc-800 border-b border-zinc-700 cursor-pointer select-none"
            style={{ minWidth: `${timelineWidth}px` }}
          >
            {/* Track label spacer - matches the track labels below */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-zinc-800 border-r border-zinc-700 z-10" />
            
            {/* Timeline content area - offset to match tracks */}
            <div 
              ref={timelineRef}
              className="ml-20 relative h-full"
              onMouseDown={handleTimelineMouseDown}
            >
              {/* Grid lines and time markers */}
              {timeMarkers.map((marker, idx) => (
                <div
                  key={idx}
                  className="absolute top-0 flex flex-col items-center"
                  style={{ left: `${(marker.time / videoDuration) * 100}%` }}
                >
                  {/* Tick mark */}
                  <div 
                    className={`w-px ${marker.isMajor ? 'h-3 bg-zinc-500' : 'h-2 bg-zinc-600'}`}
                  />
                  {/* Label */}
                  {marker.isMajor && marker.label && (
                    <span className="text-[10px] text-zinc-500 mt-0.5 font-mono whitespace-nowrap">
                      {marker.label}
                    </span>
                  )}
                </div>
              ))}
              
              {/* Playhead on ruler */}
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
                style={{ left: `${(playheadPosition / videoDuration) * 100}%` }}
              >
                <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-red-500" />
              </div>
            </div>
          </div>

          {/* Track Area */}
          <div 
            className="relative bg-zinc-850"
            style={{ minWidth: `${timelineWidth}px` }}
          >
            {/* Grid lines extending through tracks - offset by track label width */}
            <div className="ml-20 absolute inset-0 pointer-events-none">
              {timeMarkers.filter(m => m.isMajor).map((marker, idx) => (
                <div
                  key={idx}
                  className="absolute top-0 bottom-0 w-px bg-zinc-700/30"
                  style={{ left: `${(marker.time / videoDuration) * 100}%` }}
                />
              ))}
            </div>

            {/* Video Track - shows full video with trim handles */}
            <div className="relative h-12 border-b border-zinc-700">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-zinc-800 border-r border-zinc-700 flex items-center px-2 z-10">
                <Film className="w-3 h-3 text-indigo-400 mr-1" />
                <span className="text-xs text-zinc-400 truncate">Video</span>
              </div>
              <div className="ml-20 relative h-full">
                {/* Full video track background */}
                <div 
                  className="absolute top-1 bottom-1 rounded bg-gradient-to-r from-indigo-600/80 to-indigo-500/80 border border-indigo-400/30"
                  style={{ 
                    left: 0,
                    width: `${((videoDuration) / videoDuration) * 100}%`
                  }}
                />
                
                {/* Trim start overlay (dimmed area) */}
                {trimStart > 0 && (
                  <div 
                    className="absolute top-0 bottom-0 bg-zinc-900/80 z-10"
                    style={{ 
                      left: 0,
                      width: `${(trimStart / videoDuration) * 100}%`
                    }}
                  />
                )}
                
                {/* Trim end overlay (dimmed area) */}
                {trimEnd !== null && trimEnd < videoDuration && (
                  <div 
                    className="absolute top-0 bottom-0 bg-zinc-900/80 z-10"
                    style={{ 
                      left: `${(trimEnd / videoDuration) * 100}%`,
                      right: 0
                    }}
                  />
                )}
                
                {/* Trim start handle */}
                <div 
                  className="absolute top-0 bottom-0 w-2 bg-amber-500 hover:bg-amber-400 cursor-ew-resize z-20 rounded-l"
                  style={{ left: `calc(${(trimStart / videoDuration) * 100}% - 4px)` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsDraggingTrim(true);
                    const onMove = (ev) => handleTrimStartDrag(ev);
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                      setIsDraggingTrim(false);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                />
                
                {/* Trim end handle */}
                <div 
                  className="absolute top-0 bottom-0 w-2 bg-amber-500 hover:bg-amber-400 cursor-ew-resize z-20 rounded-r"
                  style={{ left: `calc(${((trimEnd || videoDuration) / videoDuration) * 100}% - 4px)` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setIsDraggingTrim(true);
                    const onMove = (ev) => handleTrimEndDrag(ev);
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                      setIsDraggingTrim(false);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                />
                
                {/* Playhead line through video track */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
                  style={{ left: `${(playheadPosition / videoDuration) * 100}%` }}
                />
              </div>
            </div>

            {/* Audio Track */}
            <div className="relative h-12">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-zinc-800 border-r border-zinc-700 flex items-center px-2 z-10">
                <Music className="w-3 h-3 text-purple-400 mr-1" />
                <span className="text-xs text-zinc-400 truncate">Audio</span>
              </div>
              <div className="ml-20 relative h-full overflow-hidden">
                {/* Audio clip - draggable, handles negative offset by showing end of audio */}
                {(() => {
                  // Calculate visible portion of audio based on offset
                  // Positive offset: audio starts later, show beginning of audio
                  // Negative offset: audio starts before video, show end portion of audio
                  const audioClipWidth = (audioDuration / videoDuration) * 100;
                  
                  // When offset is negative, the audio extends before video start
                  // We clip the beginning and show the remaining portion
                  const clippedStart = audioOffset < 0 ? Math.abs(audioOffset) : 0;
                  const visibleAudioDuration = audioDuration - clippedStart;
                  const visibleWidthPercent = (visibleAudioDuration / videoDuration) * 100;
                  
                  // Position on timeline
                  const leftPosition = audioOffset < 0 ? 0 : (audioOffset / videoDuration) * 100;
                  
                  // Waveform offset - when dragged left, show later portion of waveform
                  const waveformStartPercent = audioOffset < 0 ? (clippedStart / audioDuration) * 100 : 0;
                  
                  return (
                    <div 
                      className={`absolute top-1 bottom-1 rounded bg-gradient-to-r from-purple-600/80 to-purple-500/80 border border-purple-400/30 cursor-move overflow-hidden ${isDraggingAudio ? 'ring-2 ring-purple-400' : ''}`}
                      style={{ 
                        left: `${leftPosition}%`,
                        width: `${Math.min(visibleWidthPercent, 100 - leftPosition)}%`,
                      }}
                      onMouseDown={handleAudioDragStart}
                    >
                      {/* Actual audio waveform visualization */}
                      <div 
                        className="absolute inset-0 flex items-center overflow-hidden"
                        style={{
                          // Shift waveform left when audio is dragged left (negative offset)
                          transform: `translateX(-${waveformStartPercent}%)`,
                          width: `${(audioDuration / visibleAudioDuration) * 100}%`
                        }}
                      >
                        <div className="flex items-center h-full w-full px-1">
                          {waveformData.length > 0 ? (
                            // Real waveform from audio file
                            waveformData.map((value, i) => (
                              <div 
                                key={i}
                                className="flex-1 min-w-[1px] max-w-[4px] bg-purple-300/70 rounded-full mx-px"
                                style={{ 
                                  height: `${Math.max(10, value * 90)}%`,
                                  opacity: 0.5 + value * 0.5
                                }}
                              />
                            ))
                          ) : isLoadingWaveform ? (
                            // Loading state
                            <div className="w-full h-full flex items-center justify-center">
                              <Loader2 className="w-4 h-4 text-purple-300 animate-spin" />
                            </div>
                          ) : (
                            // Fallback generated waveform
                            [...Array(Math.min(100, Math.floor(audioDuration * 12)))].map((_, i) => (
                              <div 
                                key={i}
                                className="flex-1 min-w-[1px] max-w-[3px] bg-purple-300/60 rounded-full mx-px"
                                style={{ height: `${20 + Math.sin(i * 0.5) * 30 + Math.sin(i * 1.7) * 20}%` }}
                              />
                            ))
                          )}
                        </div>
                      </div>
                      
                      {/* Audio clip label */}
                      <div className="absolute top-0 left-1 text-[9px] text-purple-200 font-medium truncate z-10 bg-purple-600/50 px-1 rounded">
                        {voiceAsset?.name || 'Audio'}
                      </div>
                      
                      {/* Show trim indicator when audio is clipped */}
                      {audioOffset < 0 && (
                        <div className="absolute top-0 left-0 bottom-0 w-1 bg-amber-500/80" title={`${clippedStart.toFixed(1)}s trimmed from start`} />
                      )}
                    </div>
                  );
                })()}
                
                {/* Show ghost preview of clipped audio portion when offset is negative */}
                {audioOffset < 0 && (
                  <div 
                    className="absolute top-1 bottom-1 rounded bg-purple-600/20 border border-purple-400/20 border-dashed pointer-events-none"
                    style={{ 
                      right: `${100 - (0)}%`,
                      width: `${(Math.abs(audioOffset) / videoDuration) * 100}%`,
                      left: `${-((Math.abs(audioOffset) / videoDuration) * 100)}%`
                    }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center text-[8px] text-purple-400/60">
                      {Math.abs(audioOffset).toFixed(1)}s hidden
                    </div>
                  </div>
                )}
                
                {/* Playhead line through audio track */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
                  style={{ left: `${(playheadPosition / videoDuration) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="flex items-center justify-between px-4 py-2 bg-zinc-800/50 border-t border-zinc-700">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Audio Offset:</span>
                <span className="text-xs text-purple-400 font-mono w-16">
                  {audioOffset >= 0 ? '+' : ''}{audioOffset.toFixed(2)}s
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAudioOffset(0)}
                  className="text-xs text-zinc-500 hover:text-white h-6 px-2"
                >
                  Reset
                </Button>
              </div>
            </div>
            <div className="text-xs text-zinc-500">
              Drag audio clip to adjust sync • Drag yellow handles to trim video
            </div>
          </div>
            </>
          )}
          
          {/* Sync Preview Mode Info */}
          {showSyncPreview && syncPreviewUrl && (
            <div className="flex items-center justify-center px-4 py-3 bg-green-900/20 border-t border-green-700/30">
              <div className="text-sm text-green-400 text-center">
                ✓ Synced preview generated. Click &quot;Edit&quot; above to adjust timing, then regenerate preview.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LipsyncStage;
